import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Types for personalization data
export interface DetectedConcept {
  skill_code: string;
  skill_name?: string;
  confidence: number;
  performance: 'understood' | 'struggled' | 'mastered' | 'confused' | 'mentioned';
  timestamp: string;
  notes?: string;
}

export interface UserSkill {
  skill_id: string;
  skill_code: string;
  skill_name: string;
  proficiency: number;
  confidence: number;
  last_practiced?: string;
  total_attempts?: number;
  successful_attempts?: number;
}

export interface LearningProfile {
  learning_style?: 'visual' | 'auditory' | 'kinesthetic' | 'mixed';
  learner_summary?: string;
  knowledge_domains?: Record<string, number>;
  interests?: string[];
  grade_level?: string;
  occupation?: string;
  favorite_subject?: string;
  challenges?: string;
}

export interface TeachingStrategy {
  current: 'remedial' | 'adaptive' | 'socratic' | 'challenge' | 'exploratory';
  reason?: string;
  confidence?: number;
}

interface DebugState {
  // Panel state
  isOpen: boolean;
  activeTab: string;
  isPinned: boolean;
  
  // Real-time data
  detectedConcepts: DetectedConcept[];
  systemPrompt: string | null;
  teachingStrategy: TeachingStrategy | null;
  
  // User data
  userSkills: UserSkill[];
  learningProfile: LearningProfile | null;
  skillStatistics: {
    total_tracked?: number;
    mastered?: number;
    learning?: number;
    struggling?: number;
    needs_review?: number;
  } | null;
  
  // WebSocket events
  // Deprecated: wsEvents (kept for backward compatibility)
  wsEvents: Array<{
    type: string;
    data: any;
    timestamp: string;
  }>;
  // Unified live debug events (SSE or WS)
  debugEvents: Array<{
    type: string;
    data: any;
    timestamp: string;
  }>;
  
  // Settings
  maxConceptsToShow: number;
  maxEventsToShow: number;
  autoRefresh: boolean;
  showNotifications: boolean;
  
  // Actions
  setIsOpen: (isOpen: boolean) => void;
  setActiveTab: (tab: string) => void;
  setPinned: (pinned: boolean) => void;
  
  // Data actions
  addDetectedConcept: (concept: DetectedConcept) => void;
  clearDetectedConcepts: () => void;
  setSystemPrompt: (prompt: string | null) => void;
  setTeachingStrategy: (strategy: TeachingStrategy | null) => void;
  setUserSkills: (skills: UserSkill[]) => void;
  updateUserSkill: (skillId: string, updates: Partial<UserSkill>) => void;
  setLearningProfile: (profile: LearningProfile | null) => void;
  setSkillStatistics: (stats: any) => void;
  
  // WebSocket actions
  addWsEvent: (event: { type: string; data: any }) => void; // deprecated
  clearWsEvents: () => void; // deprecated
  // Unified event actions
  addEvent: (event: { type: string; data: any }) => void;
  clearEvents: () => void;
  
  // Settings actions
  updateSettings: (settings: Partial<{
    maxConceptsToShow: number;
    maxEventsToShow: number;
    autoRefresh: boolean;
    showNotifications: boolean;
  }>) => void;
  
  // Utility actions
  exportDebugData: () => void;
  resetAllData: () => void;
}

export const useDebugStore = create<DebugState>()(
  persist(
    (set, get) => ({
      // Initial state
      isOpen: false,
      activeTab: 'concepts',
      isPinned: false,
      
      detectedConcepts: [],
      systemPrompt: null,
      teachingStrategy: null,
      
      userSkills: [],
      learningProfile: null,
      skillStatistics: null,
      
      wsEvents: [],
      debugEvents: [],
      
      maxConceptsToShow: 50,
      maxEventsToShow: 100,
      autoRefresh: true,
      showNotifications: true,
      
      // Actions
      setIsOpen: (isOpen) => set({ isOpen }),
      setActiveTab: (activeTab) => set({ activeTab }),
      setPinned: (isPinned) => set({ isPinned }),
      
      // Data actions
      addDetectedConcept: (concept) => set((state) => ({
        detectedConcepts: [
          { ...concept, timestamp: concept.timestamp || new Date().toISOString() },
          ...state.detectedConcepts
        ].slice(0, state.maxConceptsToShow)
      })),
      
      clearDetectedConcepts: () => set({ detectedConcepts: [] }),
      
      setSystemPrompt: (systemPrompt) => set({ systemPrompt }),
      
      setTeachingStrategy: (teachingStrategy) => set({ teachingStrategy }),
      
      setUserSkills: (userSkills) => set({ userSkills }),
      
      updateUserSkill: (skillId, updates) => set((state) => ({
        userSkills: state.userSkills.map(skill =>
          skill.skill_id === skillId ? { ...skill, ...updates } : skill
        )
      })),
      
      setLearningProfile: (learningProfile) => set({ learningProfile }),
      
      setSkillStatistics: (skillStatistics) => set({ skillStatistics }),
      
      // WebSocket actions
      addWsEvent: (event) => set((state) => ({
        // Keep legacy list updated, but prefer debugEvents going forward
        wsEvents: [
          { ...event, timestamp: new Date().toISOString() },
          ...state.wsEvents
        ].slice(0, state.maxEventsToShow)
      })),
      clearWsEvents: () => set({ wsEvents: [] }),
      // Unified event actions
      addEvent: (event) => set((state) => ({
        debugEvents: [
          { ...event, timestamp: new Date().toISOString() },
          ...state.debugEvents
        ].slice(0, state.maxEventsToShow)
      })),
      clearEvents: () => set({ debugEvents: [] }),
      
      // Settings actions
      updateSettings: (settings) => set((state) => ({
        ...state,
        ...settings
      })),
      
      // Utility actions
      exportDebugData: () => {
        const state = get();
        const debugData = {
          timestamp: new Date().toISOString(),
          detectedConcepts: state.detectedConcepts,
          systemPrompt: state.systemPrompt,
          teachingStrategy: state.teachingStrategy,
          userSkills: state.userSkills,
          learningProfile: state.learningProfile,
          skillStatistics: state.skillStatistics,
          wsEvents: state.wsEvents,
          debugEvents: state.debugEvents,
          settings: {
            maxConceptsToShow: state.maxConceptsToShow,
            maxEventsToShow: state.maxEventsToShow,
            autoRefresh: state.autoRefresh,
            showNotifications: state.showNotifications
          }
        };
        
        // Create and download JSON file
        const blob = new Blob([JSON.stringify(debugData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `personalization-debug-${Date.now()}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      },
      
      resetAllData: () => set({
        detectedConcepts: [],
        systemPrompt: null,
        teachingStrategy: null,
        userSkills: [],
        learningProfile: null,
        skillStatistics: null,
        wsEvents: []
      })
    }),
    {
      name: 'personalization-debug-storage',
      partialize: (state) => ({
        isPinned: state.isPinned,
        activeTab: state.activeTab,
        maxConceptsToShow: state.maxConceptsToShow,
        maxEventsToShow: state.maxEventsToShow,
        autoRefresh: state.autoRefresh,
        showNotifications: state.showNotifications
      })
    }
  )
);
